import { useState, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * 요소가 화면에 처음 노출될 때와 탭 전환 후 다시 보일 때 노출 이벤트를 추적하는 훅 (앱에서 위에 쌓인 웹뷰 닫혔을 때 필요함)
 */
const useVisibilityImpression = ({
  onImpression,
  inViewOptions = {},
  enableVisibilityChange = true,
}: {
  onImpression: () => void;
  inViewOptions?: IntersectionObserverInit;
  enableVisibilityChange?: boolean;
}) => {
  const { ref, inView, entry } = useInView({ ...inViewOptions, triggerOnce: !enableVisibilityChange });
  const [isImpressed, setIsImpressed] = useState(false);

  // 최신 상태를 ref에 저장하여 이벤트 핸들러에서 접근
  const observerStateRef = useRef({ inView, entry, isImpressed });

  // ref 값 업데이트
  useEffect(() => {
    observerStateRef.current = { inView, entry, isImpressed };
  }, [inView, entry, isImpressed]);

  // 최초 한번만 노출 이벤트 발생
  useEffect(() => {
    if (inView && !isImpressed) {
      onImpression?.();
      setIsImpressed(true);
    }
  }, [inView, isImpressed, onImpression]);

  // visibilitychange 이벤트 처리 (only App)
  useEffect(() => {
    if (!enableVisibilityChange) return;

    const handleVisibilityChange = () => {
      const { entry } = observerStateRef.current;

      if (entry?.isIntersecting && document.visibilityState === 'visible') {
        onImpression?.();
        setIsImpressed(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onImpression]);

  return { ref, inView, entry };
};

export default useVisibilityImpression;
